/**
 * Client HTTP de base pour la plateforme Anamnesis
 * Wrapper autour de fetch avec gestion d'erreurs, retry et intercepteurs
 */

import { API_URL } from '@/config';
import type {
  ApiConfig,
  ApiError,
  ApiResponse,
  FastApiError,
  HttpClient,
  HttpHeaders,
  InterceptorConfig,
  RequestOptions,
  RetryOptions,
  ValidationError
} from '@/types/api';

// Configuration par défaut
const DEFAULT_CONFIG: ApiConfig = {
  baseURL: API_URL,
  timeout: 30000, // 30 secondes
  retries: 3,
  retryDelay: 1000, // 1 seconde
};

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 10000,
  retryCondition: (error: ApiError): boolean => {
    // Retry sur les erreurs réseau et serveur temporaires
    // Ne pas retry sur les erreurs 4xx (erreurs client)
    return (error.status >= 500 || error.status === 0) && error.status !== 404;
  },
};

// Headers par défaut
const DEFAULT_HEADERS: HttpHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

/**
 * Classe principale du client HTTP
 */
class ApiClient implements HttpClient {
  private config: ApiConfig;
  private interceptors: InterceptorConfig = {};

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Configure les intercepteurs
   */
  setInterceptors(interceptors: Partial<InterceptorConfig>): void {
    this.interceptors = { ...this.interceptors, ...interceptors };
  }

  /**
   * Construit l'URL complète
   */
  private buildUrl(endpoint: string): string {
    const baseUrl = this.config.baseURL.replace(/\/$/, '');
    const cleanEndpoint = endpoint.replace(/^\//, '');
    return `${baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Prépare les headers de la requête
   */
  private prepareHeaders(customHeaders: HttpHeaders = {}, isFormData: boolean = false): HttpHeaders {
    const allHeaders = {
      ...DEFAULT_HEADERS,
      ...customHeaders,
    };
    
    // Si c'est un FormData, ne pas inclure le Content-Type (fetch le gère automatiquement)
    if (isFormData) {
      delete allHeaders['Content-Type'];
    }
    
    // Filtrer les valeurs undefined
    const cleanHeaders: HttpHeaders = {};
    Object.entries(allHeaders).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanHeaders[key] = value;
      }
    });
    
    return cleanHeaders;
  }

  /**
   * Gère les erreurs HTTP et les transforme en ApiError
   */
  private async handleHttpError(response: Response, endpoint: string, method: string): Promise<ApiError> {
    const timestamp = new Date();
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let details: Record<string, unknown> = {};

    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json() as FastApiError | ValidationError;
        
        if ('detail' in errorData) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Erreur de validation (422)
            const validationErrors = errorData.detail.map(err => `${err.loc.join('.')}: ${err.msg}`);
            errorMessage = `Erreur de validation: ${validationErrors.join(', ')}`;
            details = { validationErrors: errorData.detail };
          } else {
            errorMessage = 'Erreur serveur détaillée';
            details = errorData.detail;
          }
        }
      } else {
        errorMessage = await response.text() || errorMessage;
      }
    } catch {
      // Impossible de parser la réponse d'erreur, on garde le message par défaut
    }

    const apiError: ApiError = {
      message: errorMessage,
      status: response.status,
      statusText: response.statusText,
      code: this.getErrorCode(response.status),
      details,
      timestamp,
      endpoint,
      method,
    };

    return apiError;
  }

  /**
   * Détermine le code d'erreur basé sur le status HTTP
   */
  private getErrorCode(status: number): string {
    const errorCodes: Record<number, string> = {
      400: 'BAD_REQUEST',
      401: 'UNAUTHORIZED',
      403: 'FORBIDDEN',
      404: 'NOT_FOUND',
      409: 'CONFLICT',
      422: 'VALIDATION_ERROR',
      429: 'TOO_MANY_REQUESTS',
      500: 'INTERNAL_SERVER_ERROR',
      502: 'BAD_GATEWAY',
      503: 'SERVICE_UNAVAILABLE',
    };

    return errorCodes[status] || 'HTTP_ERROR';
  }

  /**
   * Implémente la logique de retry avec backoff exponentiel
   */
  private async executeWithRetry<T>(
    fn: () => Promise<ApiResponse<T>>,
    options: RetryOptions = DEFAULT_RETRY_OPTIONS
  ): Promise<ApiResponse<T>> {
    let lastError: ApiError;
    
    for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as ApiError;
        
        // Si ce n'est pas la dernière tentative et que l'erreur est retryable
        if (attempt < options.maxRetries && options.retryCondition(lastError)) {
          const delay = Math.min(
            options.delayMs * Math.pow(options.backoffMultiplier, attempt),
            options.maxDelayMs
          );
          
          console.warn(`Tentative ${attempt + 1}/${options.maxRetries + 1} échouée, retry dans ${delay}ms:`, lastError.message);
          await this.sleep(delay);
          continue;
        }
        
        throw lastError;
      }
    }
    
    throw lastError!;
  }

  /**
   * Utilitaire pour pause async
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Exécute une requête HTTP
   */
  private async request<T>(endpoint: string, options: RequestOptions): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);
    const isFormData = options.body instanceof FormData;
    const headers = this.prepareHeaders(options.headers, isFormData);
    

    
    // Intercepteur de requête
    const finalOptions = this.interceptors.onRequest 
      ? await this.interceptors.onRequest({ ...options, headers })
      : { ...options, headers };

    // Configuration de fetch
    const fetchOptions: RequestInit = {
      method: finalOptions.method,
      headers: finalOptions.headers as HeadersInit,
      signal: finalOptions.signal,
    };

    // Ajout du body pour les méthodes qui le supportent
    if (finalOptions.body && ['POST', 'PUT', 'PATCH'].includes(finalOptions.method)) {
      if (finalOptions.body instanceof FormData) {
        fetchOptions.body = finalOptions.body;
      } else {
        fetchOptions.body = typeof finalOptions.body === 'string' 
          ? finalOptions.body 
          : JSON.stringify(finalOptions.body);
      }
    }



    // Timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), finalOptions.timeout || this.config.timeout);
    
    if (!fetchOptions.signal) {
      fetchOptions.signal = controller.signal;
    }

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // Gestion des erreurs HTTP
      if (!response.ok) {
        const error = await this.handleHttpError(response, endpoint, finalOptions.method);
        
        // Intercepteur d'erreur
        if (this.interceptors.onError) {
          await this.interceptors.onError(error);
        }
        
        throw error;
      }

      // Parse de la réponse
      let data: T;
      const contentType = response.headers.get('content-type');
      const isNoContent = response.status === 204 || response.status === 205 || finalOptions.method === 'DELETE';

      if (isNoContent) {
        // Certaines API renvoient 204 No Content (ou DELETE sans body)
        // Éviter toute tentative de parse du body
        data = undefined as unknown as T;
      } else if (contentType?.includes('application/json')) {
        data = await response.json() as T;
      } else if (contentType?.includes('text/')) {
        data = await response.text() as unknown as T;
      } else {
        data = await response.blob() as unknown as T;
      }

      // Construction de la réponse standardisée
      const apiResponse: ApiResponse<T> = {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        success: true,
      };

      // Intercepteur de réponse
      return this.interceptors.onResponse 
        ? await this.interceptors.onResponse(apiResponse)
        : apiResponse;

    } catch (error) {
      clearTimeout(timeoutId);
      
      // Gestion des erreurs réseau
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          const timeoutError: ApiError = {
            message: 'Délai d\'attente dépassé',
            status: 0,
            statusText: 'Timeout',
            code: 'TIMEOUT',
            timestamp: new Date(),
            endpoint,
            method: finalOptions.method,
          };
          throw timeoutError;
        }
        
        const networkError: ApiError = {
          message: error.message || 'Erreur réseau',
          status: 0,
          statusText: 'Network Error',
          code: 'NETWORK_ERROR',
          timestamp: new Date(),
          endpoint,
          method: finalOptions.method,
        };
        throw networkError;
      }
      
      throw error;
    }
  }

  // Méthodes HTTP publiques

  async get<T>(endpoint: string, options: Partial<RequestOptions> = {}): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => 
      this.request<T>(endpoint, { method: 'GET', ...options })
    );
  }

  async post<T>(endpoint: string, data?: unknown, options: Partial<RequestOptions> = {}): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => 
      this.request<T>(endpoint, { method: 'POST', body: data, ...options })
    );
  }

  async put<T>(endpoint: string, data?: unknown, options: Partial<RequestOptions> = {}): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => 
      this.request<T>(endpoint, { method: 'PUT', body: data, ...options })
    );
  }

  async patch<T>(endpoint: string, data?: unknown, options: Partial<RequestOptions> = {}): Promise<ApiResponse<T>> {
    return this.executeWithRetry(() => 
      this.request<T>(endpoint, { method: 'PATCH', body: data, ...options })
    );
  }

  async delete<T>(endpoint: string, options: Partial<RequestOptions> = {}): Promise<ApiResponse<T>> {
    // Ne pas retry sur les suppressions pour éviter les doubles suppressions
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  async upload<T>(endpoint: string, options: { file: File; fieldName: string; additionalFields?: Record<string, string> }): Promise<ApiResponse<T>> {
    console.log('[apiClient] upload appelé avec:', {
      endpoint,
      fileName: options.file.name,
      fileSize: options.file.size,
      fileType: options.file.type,
      fieldName: options.fieldName,
      additionalFields: options.additionalFields
    });

    const formData = new FormData();
    formData.append(options.fieldName, options.file);
    
    if (options.additionalFields) {
      Object.entries(options.additionalFields).forEach(([key, value]) => {
        console.log(`[apiClient] Ajout du champ: ${key} = ${value}`);
        formData.append(key, value);
      });
    }

    // Log du FormData pour debug
    console.log('[apiClient] FormData créé, entrées:');
    for (const [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
    }

    const uploadOptions: RequestOptions = {
      method: 'POST',
      body: formData,
      headers: {
        // Ne pas définir Content-Type pour FormData (fetch le fait automatiquement)
        'Accept': 'application/json',
      },
    };

    console.log('[apiClient] Options de requête:', {
      method: uploadOptions.method,
      headers: uploadOptions.headers,
      bodyType: uploadOptions.body?.constructor.name
    });

    return this.request<T>(endpoint, uploadOptions);
  }
}

// Instance singleton
export const apiClient = new ApiClient();

// Export pour test et configuration
export { ApiClient };
export type { ApiConfig, ApiError, ApiResponse };

